import { createApiClient } from '@thecompaniesapi/api-helpers'
import type { AxiosError } from 'axios'

export default defineNuxtPlugin(() => {
  const { createToast } = useToasts()
  const config = useRuntimeConfig()
  const localePath = useLocalePath()
  const opaqueToken = useCookie('opaqueToken')
  const nuxtApp = useNuxtApp()
  const visitorId = useLocalStorage('visitorId', '')
  const { toggleModal } = useOverlays()

  // Initialize Api Helpers and set interceptors
  const api = createApiClient({ url: config.public.apis.public })

  api.client.interceptors.request.use((config) => {
    config.headers['Accept-Language'] = (nuxtApp.$i18n as any).locale.value || 'en'
    config.headers['Content-Type'] = 'application/json'

    const authStore = useAuthStore()
    const token = authStore.opaqueToken

    if (visitorId.value) {
      config.headers['Tca-Visitor-Id'] = visitorId.value
    }

    if (!config.headers.Authorization) {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      else {
        delete config.headers.Authorization
      }
    }

    return config
  })

  api.client.interceptors.response.use(
    (response) => {
      if (response.status === 200 && response.data?.meta?.credits) {
        const teamStore = useTeamsStore()

        const credits = response.data.meta.credits

        // Update the current team
        if (teamStore.currentTeam) {
          teamStore.currentTeam.credits = credits
        }
      }

      return response
    },
    async (error: AxiosError<{ [key: string]: any }>) => {
      // Opaque Token is invalid or expired
      if (
        error.response?.status === 401
        || error.response?.data?.message === 'invalidOpaqueToken'
        || error.response?.data?.message === 'tokenNotFound'
        || error.response?.data?.message === 'userNotAuthenticated'
      ) {
        opaqueToken.value = undefined

        // Navigate to logout page if on client
        if (import.meta.client) {
          createToast({
            debounce: 500,
            message: 'invalidOpaqueToken',
            status: 'error',
          })

          await navigateTo(localePath('auth-logout'))
        }

        return {}
      }

      if (import.meta.client) {
        if (error.response?.status === 429) {
          const headers = (error.response as any)?.headers.toJSON()
          const rateLimitCount = Number(headers['x-ratelimit-limit'])
          const rateLimitReset = new Date(headers['x-ratelimit-reset'])

          createToast({
            debounce: 500,
            message: 'tooManyRequests',
            status: 'error',
          })

          toggleModal({
            data: {
              rateLimitCount,
              rateLimitReset,
            },
            opened: true,
            title: 'tooManyRequests',
            type: 'subscription',
          })

          return {}
        }
        else if (error.response?.data?.message) {
          createToast({
            debounce: 500,
            message: error.response.data.message,
            status: 'error',
          })
        }

        const errors = error.response?.data?.errors || []

        if (errors) {
          for (const error of errors) {
            createToast({
              debounce: 500,
              message: error,
              status: 'error',
            })
          }
        }
      }

      return Promise.reject(error)
    },
  )

  return { provide: { api } }
})
